import React from 'react';
import { Button, Typography, Space } from 'antd';
import { motion } from 'framer-motion';
import {
  ShoppingCartOutlined,
  ShopOutlined,
  TeamOutlined,
  SearchOutlined,
  FileOutlined,
  InboxOutlined,
  PlusOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

// Floating animation for icons
const floatAnimation = {
  animate: {
    y: [0, -8, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

// Icon configurations for different contexts
const iconConfigs = {
  orders: {
    icon: ShoppingCartOutlined,
    color: '#1890ff',
    bgColor: '#e6f7ff',
    title: 'Henuz siparis yok',
    description: 'Siparislerinizi takip etmeye baslayin',
    buttonText: 'Ilk Siparisi Olustur',
  },
  companies: {
    icon: ShopOutlined,
    color: '#722ed1',
    bgColor: '#f9f0ff',
    title: 'Sirket bulunamadi',
    description: 'Sisteme ilk sirketinizi ekleyin',
    buttonText: 'Sirket Ekle',
  },
  employees: {
    icon: TeamOutlined,
    color: '#faad14',
    bgColor: '#fffbe6',
    title: 'Calisan bulunamadi',
    description: 'Ekibinizi olusturmaya baslayin',
    buttonText: 'Calisan Ekle',
  },
  search: {
    icon: SearchOutlined,
    color: '#8c8c8c',
    bgColor: '#fafafa',
    title: 'Sonuc bulunamadi',
    description: 'Farkli filtreler denemeyi deneyin',
    buttonText: 'Filtreleri Temizle',
  },
  orderItems: {
    icon: FileOutlined,
    color: '#52c41a',
    bgColor: '#f6ffed',
    title: 'Siparis kalemi yok',
    description: 'Bu siparise kalem ekleyin',
    buttonText: 'Kalem Ekle',
  },
  default: {
    icon: InboxOutlined,
    color: '#8c8c8c',
    bgColor: '#fafafa',
    title: 'Veri bulunamadi',
    description: 'Burada henuz icerik yok',
    buttonText: 'Yeni Ekle',
  }
};

const EmptyState = ({
  type = 'default',
  title,
  description,
  buttonText,
  onAction,
  showButton = true,
  extra,
  size = 'default' // 'small', 'default', 'large'
}) => {
  const config = iconConfigs[type] || iconConfigs.default;
  const IconComponent = config.icon;

  const displayTitle = title || config.title;
  const displayDescription = description || config.description;
  const displayButtonText = buttonText || config.buttonText;

  const sizes = {
    small: { icon: 48, padding: 24 },
    default: { icon: 72, padding: 48 },
    large: { icon: 96, padding: 64 }
  };

  const currentSize = sizes[size];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
      style={{
        textAlign: 'center',
        padding: currentSize.padding,
      }}
    >
      {/* Animated Icon Container */}
      <motion.div
        variants={floatAnimation}
        animate="animate"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: currentSize.icon * 1.5,
          height: currentSize.icon * 1.5,
          borderRadius: '50%',
          backgroundColor: config.bgColor,
          marginBottom: 24,
          position: 'relative',
        }}
      >
        {/* Background circles for depth effect */}
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            border: `2px dashed ${config.color}20`,
            transform: 'scale(1.3)',
          }}
        />
        <IconComponent
          style={{
            fontSize: currentSize.icon,
            color: config.color,
          }}
        />
      </motion.div>

      {/* Text Content */}
      <Space direction="vertical" size={8} style={{ marginBottom: 24 }}>
        <Title
          level={size === 'small' ? 5 : 4}
          style={{
            margin: 0,
            color: '#262626',
          }}
        >
          {displayTitle}
        </Title>
        <Text
          type="secondary"
          style={{
            fontSize: size === 'small' ? 13 : 14,
            maxWidth: 300,
            display: 'inline-block',
          }}
        >
          {displayDescription}
        </Text>
      </Space>

      {/* Action Button */}
      {showButton && onAction && (
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={onAction}
            size={size === 'small' ? 'middle' : 'large'}
            style={{
              backgroundColor: config.color,
              borderColor: config.color,
              boxShadow: `0 4px 12px ${config.color}30`,
            }}
          >
            {displayButtonText}
          </Button>
        </motion.div>
      )}

      {/* Extra content */}
      {extra && (
        <div style={{ marginTop: 16 }}>
          {extra}
        </div>
      )}
    </motion.div>
  );
};

// Inline Empty State for smaller contexts (like inside cards)
export const InlineEmptyState = ({
  type = 'default',
  message,
  onAction,
  actionText,
}) => {
  const config = iconConfigs[type] || iconConfigs.default;
  const IconComponent = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px 24px',
        backgroundColor: config.bgColor,
        borderRadius: 8,
        gap: 12,
      }}
    >
      <IconComponent style={{ fontSize: 20, color: config.color }} />
      <Text type="secondary">{message || config.description}</Text>
      {onAction && (
        <Button
          type="link"
          size="small"
          onClick={onAction}
          style={{ color: config.color, padding: '0 8px' }}
        >
          {actionText || 'Ekle'}
        </Button>
      )}
    </motion.div>
  );
};

// Search Empty State - specifically for no search results
export const SearchEmptyState = ({
  searchTerm,
  onClearFilters,
  suggestions = []
}) => (
  <EmptyState
    type="search"
    title={searchTerm ? `"${searchTerm}" icin sonuc bulunamadi` : 'Sonuc bulunamadi'}
    description="Arama kriterlerinizi degistirmeyi deneyin"
    buttonText="Filtreleri Temizle"
    onAction={onClearFilters}
    showButton={!!onClearFilters}
    extra={
      suggestions.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">Oneriler: </Text>
          {suggestions.map((s, i) => (
            <Button key={i} type="link" size="small" onClick={() => s.onClick?.()}>
              {s.text}
            </Button>
          ))}
        </div>
      )
    }
  />
);

export default EmptyState;
